import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { MatDividerModule } from '@angular/material/divider';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  map,
  Observable,
  of,
  OperatorFunction,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { RolesDialogComponent } from './roles-dialog/roles-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeleteRoleModalComponent } from './delete-role-modal/delete-role-modal.component';
import { RoleResponse } from './interfaces/role-response.interface';
import { RoleRequest } from './interfaces/role.request.interface';
import { RolesService } from './services/roles.service';
import { ROLES_DIALOG_CONFIG } from './roles-dialog/roles-dialog.config';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { TablePaginationComponent } from '@app/components/table-pagination/table-pagination.component';
import {
  FAKE_ROLES_QUANTITY,
  ROLES_COLUMNS_CONFIG,
  ROLES_OPTIONS,
} from '@app/utils/constants/roles-settings.constants';
import { TABLE_PAGINATION_DEFAULT_CONFIG } from '@app/utils/constants/table-pagination.constants';
import { IconName } from '@app/utils/enums/icon-enum';
import { RolesEnumList } from '@app/utils/enums/roles-columns';
import { TablePaginationConfig } from '@app/utils/interfaces/table-pagination.interface';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { UserStateService } from '@app/services/user-state/user-state.service';
import { HttpStatusCode } from '@app/utils/enums/http-status-codes.enum';
import { LockService } from '@app/services/lock/lock.service';
import { LockPayload } from '@app/utils/interfaces/lock.interface';
import { LockableEntities } from '@app/utils/enums/lock.enum';
import { SnackbarService } from '@app/services/snackbar/snackbar.service';
import { SnackbarStatus } from '@app/utils/enums/snackbar.enum';

@Component({
  selector: 'app-settings-roles',
  standalone: true,
  imports: [
    MatTableModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    TranslateModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    TablePaginationComponent,
    IconComponent,
  ],
  templateUrl: './settings-roles.component.html',
  styleUrl: './settings-roles.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsRolesComponent implements OnInit {
  private readonly rolesService = inject(RolesService);
  private readonly userStateService = inject(UserStateService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly lockService = inject(LockService);
  private lockedRole: LockPayload | null = null;
  private readonly snackbarService = inject(SnackbarService);

  protected readonly displayedColumns: RolesEnumList[] = Object.values(RolesEnumList);
  protected readonly roles = signal<RoleResponse[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly isUpdatingRoles = signal(false);
  protected readonly skeletonRows = signal<Partial<RoleResponse>[]>(
    Array(FAKE_ROLES_QUANTITY).fill({}),
  );
  protected readonly IconName = IconName;
  protected readonly columnsConfig = ROLES_COLUMNS_CONFIG;
  protected readonly ADMIN_ROLE = 'Admin';
  private readonly stopRefresh$ = new Subject<void>();

  protected settingsRolesPagination = signal(TABLE_PAGINATION_DEFAULT_CONFIG);

  public ngOnInit(): void {
    this.loadRoles();
  }

  protected onAddRole(newRole: RoleRequest): void {
    const { currentPage, pageSize } = this.settingsRolesPagination();

    this.isUpdatingRoles.set(true);

    this.rolesService
      .addRole(newRole)
      .pipe(
        switchMap(() => this.rolesService.getRoles(currentPage, pageSize)),
        tap((data) => {
          this.settingsRolesPagination.update((prev) => ({
            ...prev,
            totalItems: data.data.totalAmount,
          }));
        }),
        map((data) => data.data.items),
        finalize(() => this.isUpdatingRoles.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (roles) => this.roles.set(roles),
        error: () =>
          this.snackbarService.openSnackbar(
            SnackbarStatus.FAIL,
            'snackbar.edit-role.general-error',
          ),
      });
  }

  protected tryToHandleRoleModification(
    role: RoleResponse,
    modifyAction: (role: RoleResponse) => void,
  ): void {
    this.lockedRole = {
      entityToLock: LockableEntities.ROLE,
      entityId: role.id.toString(),
    };

    this.lockService
      .check(this.lockedRole)
      .pipe(
        switchMap((isRoleLocked: boolean) => {
          if (!isRoleLocked) {
            return this.lockService.lock(this.lockedRole!).pipe(
              tap(() => modifyAction(role)),
              this.enableRefresh(this.lockedRole!),
            );
          }

          this.openLoadRolesErrorModal(
            'shop-settings.roles.errors.errorTitle',
            'shop-settings.roles.errors.modificationConflict',
          );

          return EMPTY;
        }),
        catchError((error) => {
          this.lockedRole = null;
          return this.handleRoleRequestError(error);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected onEditRole(updatedRole: RoleRequest): void {
    if (!updatedRole?.id) return;

    const { name, description, permissionIds } = updatedRole;
    const { currentPage, pageSize } = this.settingsRolesPagination();
    this.isUpdatingRoles.set(true);

    this.rolesService
      .updateRole({ name, description, permissionIds }, updatedRole.id)
      .pipe(
        switchMap(() => this.rolesService.getRoles(currentPage, pageSize)),
        map((data) => data.data.items),
        finalize(() => this.isUpdatingRoles.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (roles) => {
          this.roles.set(roles);
          this.snackbarService.openSnackbar(
            SnackbarStatus.SUCCESS,
            'snackbar.edit-role.edit-successful',
          );
        },
        error: () => {
          this.snackbarService.openSnackbar(
            SnackbarStatus.SUCCESS,
            'snackbar.edit-role.general-error',
          );
        },
      });
  }

  protected onDelete(role: RoleResponse): void {
    if (this.isCurrentUserRole(role)) {
      this.openLoadRolesErrorModal(
        'shop-settings.roles.rolesModalDelete.errors.errorTitle',
        'shop-settings.roles.rolesModalDelete.errors.errorActiveStaffMessage',
      );

      return;
    }

    const { currentPage, pageSize } = this.settingsRolesPagination();

    this.openDeleteConfirmation(role)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(Boolean),
        tap(() => this.isUpdatingRoles.set(true)),
        switchMap(() =>
          this.rolesService
            .deleteRole(role.id)
            .pipe(catchError((error) => this.handleRoleRequestError(error))),
        ),
        switchMap(() => this.rolesService.getRoles(currentPage, pageSize)),
        tap((data) => {
          const totalItems = data.data.totalAmount;
          const totalPages = Math.ceil(totalItems / pageSize);

          this.settingsRolesPagination.update((prev) => ({
            ...prev,
            totalItems,
            currentPage: Math.min(totalPages, prev.currentPage),
          }));
        }),
        map((data) => data.data.items),
        tap((roles) => this.roles.set(roles)),
        switchMap(() => {
          if (this.lockedRole !== null) {
            return this.lockService.deleteLock(this.lockedRole);
          }

          return EMPTY;
        }),
        finalize(() => {
          this.isUpdatingRoles.set(false);
          this.lockedRole = null;
        }),
      )
      .subscribe({
        error: () => {
          this.openLoadRolesErrorModal();
        },
      });
  }

  protected openAddDialog(): void {
    const dialogRef = this.dialog.open(RolesDialogComponent, {
      ...ROLES_DIALOG_CONFIG,
      data: { role: null, isEdit: false },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onAddRole(result);
      }
    });
  }

  protected openEditDialog(role: RoleResponse): void {
    const dialogRef = this.dialog.open(RolesDialogComponent, {
      ...ROLES_DIALOG_CONFIG,
      data: { role: role, isEdit: true },
    });

    dialogRef
      .afterClosed()
      .pipe(
        tap((result) => {
          if (result) {
            const updatedRole: RoleRequest = {
              ...result,
              id: role.id,
              userCount: role.userCount,
            };
            this.onEditRole(updatedRole);
          }

          this.stopRefresh$.next();
        }),
        switchMap(() => {
          if (this.lockedRole) {
            return this.lockService.deleteLock(this.lockedRole);
          }

          return EMPTY;
        }),
        tap(() => {
          this.lockedRole = null;
        }),
      )
      .subscribe();
  }

  protected loadRoles(): void {
    this.isLoading.set(true);

    const { currentPage, pageSize } = this.settingsRolesPagination();

    this.rolesService
      .getRoles(currentPage, pageSize)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((data) => {
          this.settingsRolesPagination.update((prevState) => ({
            ...prevState,
            totalItems: data.data.totalAmount,
          }));

          return this.addMockUsersToRoles(data.data.items);
        }),
        tap(() => {
          this.isLoading.set(false);
        }),
        catchError(() => {
          this.openLoadRolesErrorModal();

          return EMPTY;
        }),
      )
      .subscribe((response) => {
        this.roles.set(response);
      });
  }

  protected handlePageChange(page: number): void {
    this.updatePagination({ currentPage: page });
  }

  protected handlePageSizeChange(size: number): void {
    this.updatePagination({ pageSize: size, currentPage: 1 });
  }

  private addMockUsersToRoles(roles: RoleResponse[]): RoleResponse[] {
    return roles.map((role) => ({
      ...role,
      users: Array.from({ length: role.userCount }).map((_, idx) => ({
        id: idx + 1,
        firstName: `Username${idx + 1}`,
        lastName: `${role.name}LastName${idx + 1}`,
      })),
    }));
  }

  private updatePagination(paginationConfig: Partial<TablePaginationConfig>): void {
    this.settingsRolesPagination.update((prevState) => ({
      ...prevState,
      ...paginationConfig,
    }));

    this.loadRoles();
  }

  private isCurrentUserRole(role: RoleResponse): boolean {
    return this.userStateService.userData()?.data?.roleId === role.id;
  }

  private openDeleteConfirmation(role: RoleResponse) {
    return this.dialog
      .open(DeleteRoleModalComponent, {
        ...ROLES_OPTIONS,
        data: {
          error: role.userCount > 0,
          roleName: role.name,
        },
      })
      .afterClosed()
      .pipe(
        tap(() => this.stopRefresh$.next()),
        switchMap((value: boolean) => {
          if (!value && this.lockedRole !== null) {
            return this.lockService.deleteLock(this.lockedRole);
          }

          return of(value);
        }),
        tap((value) => {
          if (!value) {
            this.lockedRole = null;
          }
        }),
      );
  }

  private openLoadRolesErrorModal(
    title = 'shop-settings.roles.errors.errorTitle',
    description = 'shop-settings.roles.errors.errorText',
  ): void {
    this.dialog.open(StatusModalComponent, {
      ...ROLES_OPTIONS,
      data: {
        isSuccess: false,
        title,
        description,
      },
    });
  }

  private handleRoleRequestError(error: HttpErrorResponse): typeof EMPTY {
    if (error.status === HttpStatusCode.CONFLICTS) {
      const message = error.error?.message ?? '';

      if (message.includes('This role is currently assigned to active staff')) {
        this.openLoadRolesErrorModal(
          'shop-settings.roles.rolesModalDelete.errors.errorTitle',
          'shop-settings.roles.rolesModalDelete.errors.errorActiveStaffMessage',
        );

        return EMPTY;
      }
    }

    if (error.status === HttpStatusCode.NOT_FOUND) {
      this.openLoadRolesErrorModal(
        'shop-settings.roles.rolesModalDelete.errors.errorTitle',
        'shop-settings.roles.rolesModalDelete.errors.errorAlreadyDeletedMessage',
      );

      return EMPTY;
    }

    this.openLoadRolesErrorModal();

    return EMPTY;
  }

  private enableRefresh(lockPayload: LockPayload): OperatorFunction<void, void> {
    // Backend locks for 5 minutes, we try to refresh every 4:50.
    const REFRESH_PERIOD = 290_000;

    return switchMap<void, Observable<void>>(() =>
      timer(REFRESH_PERIOD, REFRESH_PERIOD).pipe(
        switchMap(() => this.lockService.refreshLock(lockPayload)),
        takeUntil(this.stopRefresh$),
      ),
    );
  }
}
